import React, { ReactNode } from 'react';

/**
 * `common/Badge` (M-UX-2.3, architecture-ux-restructure.md §4.3).
 *
 * Tone scale is intentionally identical to `InboundBadgeTone`
 * (`utils/inboundAdapters.ts:143-148`, consumed by `InboundBadgeList.tsx`) —
 * one visual language for status pills across the app. This component does
 * NOT replace `InboundBadgeList` (that keeps its i18n-descriptor contract);
 * it's the general-purpose primitive for every other status/tone pill.
 */
export type BadgeTone = 'success' | 'info' | 'warning' | 'neutral' | 'caution' | 'danger';

export interface BadgeProps {
  tone: BadgeTone;
  children: ReactNode;
  className?: string;
}

/**
 * Tone → utility-class map. Exported (M-A, architecture-ux-density-v2.md §5.1/§5.2)
 * so `StatusChip` reuses the exact same tone colour language instead of forking
 * the hex values — the chip IS visually a Badge with interaction. `Record<BadgeTone,…>`
 * makes the compiler demand the new `danger` key here whenever the union grows.
 *
 * v5 (architecture-ux-design-v5.md §5): values are Tailwind arbitrary-value
 * classes referencing the `--tone-{tone}-{bg,fg,border}` CSS vars defined in
 * `styles/tokens.css` (`:root` = dark rezept, `[data-theme="light"]` =
 * light rezept) — so the theme flip is token-only and this map is touched
 * exactly once. Mint is deliberately absent from every tone: `success` is
 * now Laub (green ramp), not the brand Mint (v5 §1.3 "Marke ≠ Status").
 */
export const TONE_CLASSES: Record<BadgeTone, string> = {
  success: 'bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)] border-[var(--tone-success-border)]',
  info: 'bg-[var(--tone-info-bg)] text-[var(--tone-info-fg)] border-[var(--tone-info-border)]',
  warning: 'bg-[var(--tone-warning-bg)] text-[var(--tone-warning-fg)] border-[var(--tone-warning-border)]',
  neutral: 'bg-[var(--tone-neutral-bg)] text-[var(--tone-neutral-fg)] border-[var(--tone-neutral-border)]',
  caution: 'bg-[var(--tone-caution-bg)] text-[var(--tone-caution-fg)] border-[var(--tone-caution-border)]',
  danger: 'bg-[var(--tone-danger-bg)] text-[var(--tone-danger-fg)] border-[var(--tone-danger-border)]',
};

/**
 * Shared base classes for the badge/chip "stamp" shape (reused by StatusChip).
 * v5 (§5): `rounded-full` pill → `rounded` stamp, `font-medium` +
 * `tracking-[0.01em]` moved here from the per-tone `success` string (applies
 * to every tone now, not just success).
 */
export const BADGE_BASE_CLASSES = 'inline-flex items-center gap-1 text-xs px-2 py-0.5 border rounded font-medium tracking-[0.01em]';

export function Badge({ tone, children, className = '' }: BadgeProps) {
  return (
    <span
      className={[
        BADGE_BASE_CLASSES,
        TONE_CLASSES[tone],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </span>
  );
}

export default Badge;
